import { FC } from 'react';
import * as React from 'react';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import { Select } from '@zoopla-eng/zds/select';
import { triggerTracking } from '@zoopla/tracking';
import { updateResults } from '@zoopla/listing-search-update-results';
import {
    updateCurrentSortOrder,
    selectSortOrderOptions,
    selectCurrentSortOrder
} from '@zoopla/redux-reducer-sort-order';
import { filtersValuesSelector } from '@zoopla/redux-reducer-filters';
import { Action as SearchContextAction } from '@zoopla/listing-search-context';
import { SearchPageType } from '@zoopla/listing-search-context';

const sortOrderAriaLabel = 'Sort';

type SortOrderDropdownProps = {
    excludeRadiusFromResults: boolean;
    userAlertId: string | null;
    searchType: SearchPageType;
    dispatch: React.Dispatch<SearchContextAction>;
};

export const SortOrderDropdown: FC<
    React.PropsWithChildren<SortOrderDropdownProps>
> = ({ excludeRadiusFromResults, userAlertId, searchType, dispatch }) => {
    const reduxDispatch = useDispatch();

    const filters = useSelector(filtersValuesSelector);
    const options = useSelector(selectSortOrderOptions);
    const currentSortOrderValue = useSelector(selectCurrentSortOrder);
    const router = useRouter();

    const onChange = (option: { id: string }) => {
        reduxDispatch(updateCurrentSortOrder(option.id));
        triggerTracking({
            event: 'GAEvent',
            eventCategory: 'search results',
            eventAction: 'click',
            eventLabel:
                option.id === 'newest_listings'
                    ? 'sort_most_recent'
                    : `sort_${option.id}`
        });
        if (userAlertId) {
            dispatch({
                type: 'SET_PREVIOUS_SAVED_SEARCH_URI',
                payload: router.asPath
            });
        }

        updateResults(
            filters,
            router,
            searchType,
            'refine',
            option.id,
            userAlertId,
            excludeRadiusFromResults,
            true
        );
    };

    const selectOptions =
        options?.map((option) => ({
            id: option.value,
            text: option.label
        })) ?? [];

    const currentSortOrder = selectOptions.find(
        (o) => o.id === currentSortOrderValue
    );

    if (typeof selectOptions[0] === 'undefined') {
        return null;
    }

    return (
        <Select
            data-testid="sort-order-dropdown"
            label="Sort order"
            ariaLabel={sortOrderAriaLabel}
            selectedOption={currentSortOrder ?? selectOptions[0]}
            onSelectedOptionChange={onChange}
            options={selectOptions}
            labelAlignment="left"
        />
    );
};
